import type { ReactNode } from 'react';

import { BrandLockup } from '../marketing/brand-lockup';

/** The showcase's decorations, as delivered by design. The folder name has a
 *  space in it, hence the encoding. */
export const SHOWCASE_ASSETS = '/Sign%20in';

/**
 * The frame "/login" and "/login/onboarding" share: the logo and a form column
 * on the left, a violet showcase on the right.
 *
 * One screen tall on desktop — the showcase fills the height and only the form
 * column scrolls, if it has to. Below `lg` the showcase is dropped and the form
 * keeps the whole screen.
 */
export function AuthLayout({
  children,
  showcase,
}: {
  children: ReactNode;
  showcase: ReactNode;
}) {
  return (
    <main className="flex min-h-svh bg-surface lg:h-svh">
      <section className="flex flex-1 flex-col px-6 py-6 lg:overflow-y-auto lg:px-12">
        <header>
          <BrandLockup />
        </header>
        {children}
      </section>
      {showcase}
    </main>
  );
}

/**
 * The violet panel: pill, headline, the product skeleton and the wavy line —
 * everything both screens have. What differs (the people, the yellow shapes)
 * comes in as children, laid out absolutely against the panel.
 *
 * Decorative, so hidden from assistive tech.
 */
export function ShowcasePanel({
  children,
  title,
}: {
  children: ReactNode;
  title: ReactNode;
}) {
  return (
    <aside
      aria-hidden="true"
      className="hidden w-2/5 max-w-xl shrink-0 p-4 select-none lg:block"
    >
      <div className="relative h-full overflow-hidden rounded-panel bg-room-violet">
        {/* The pill and headline at full size, as in the design — outside the
            zoomed scene below, which would shrink them to 75% too. */}
        <div className="relative z-10 px-14 pt-13">
          <span className="inline-flex h-[34px] items-center gap-2 rounded-pill bg-white/16 px-3.5 text-ui font-semibold text-white">
            <span className="size-2 rounded-pill bg-sunny" />
            Rooms by Floto
          </span>
          <h2 className="!mt-[15px] !mb-0 max-w-[7.4em] !text-auth-showcase !font-bold !text-white">
            {title}
          </h2>
        </div>

        {/* The scene drawn at 75%, so it fits a laptop screen without
            scrolling. `zoom` rather than a scale transform: it re-lays out, so
            percentage positions still resolve against the panel's real size.
            Above the headline: the cards start below where it wraps to, and
            if a short window still brings them together, a card covering the
            headline reads as layered; white text through a white card reads
            as broken. */}
        <div className="absolute inset-0 isolate z-20 px-10 pt-10 [zoom:0.75] xl:px-12">
          <img
            alt=""
            className="absolute right-12 bottom-8 -z-10 w-48"
            src={`${SHOWCASE_ASSETS}/wavy-line.svg`}
          />
          <img
            alt=""
            className="absolute top-[42%] left-[16%] -z-10 w-2/3 max-w-md"
            src={`${SHOWCASE_ASSETS}/bg-skeleton.svg`}
          />

          {children}
        </div>
      </div>
    </aside>
  );
}
